import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Autocomplete,
  Avatar,
  Box,
  Chip,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import {
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { Alert, Card, CardContent, CardHeader, Stack } from "../../design-system";
import { colors, spacing } from "../../design-system";

const CHART_COLORS = [
  "var(--sc-series-one)",
  "var(--sc-series-two)",
  "var(--sc-series-three)",
];

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) {
    return null;
  }

  const metricLabel = payload[0]?.payload?.metric;
  const rawValues = payload[0]?.payload?.rawValues || {};

  return (
    <Paper
      sx={{
        p: 1.5,
        borderRadius: spacing.borderRadius.md,
        border: `1px solid ${colors.primary[200]}`,
      }}
    >
      <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>
        {metricLabel}
      </Typography>
      {payload.map((entry) => (
        <Typography key={entry.name} variant="caption" sx={{ display: "block", color: entry.color }}>
          {entry.name}: <strong>{rawValues[entry.name] ?? 0}</strong>
        </Typography>
      ))}
    </Paper>
  );
};

const PlayerComparisonChart = ({ players = [] }) => {
  const { t } = useTranslation('scouting');
  const METRICS = useMemo(() => [
    { key: "goals", label: t('components.trends.metric.goals') },
    { key: "starters", label: t('components.trends.metric.starters') },
    { key: "cards", label: t('components.trends.metric.cards') },
    { key: "minutes", label: t('components.trends.metric.minutes') },
    { key: "matches", label: t('components.trends.metric.matches') },
  ], [t]);
  const [selectedIds, setSelectedIds] = useState([]);

  useEffect(() => {
    setSelectedIds((current) => {
      const validIds = current.filter((id) => players.some((player) => player.id === id)).slice(0, 3);
      if (validIds.length >= 1 || players.length === 0) {
        return validIds;
      }
      return players.slice(0, Math.min(3, players.length)).map((player) => player.id);
    });
  }, [players]);

  const selectedPlayers = useMemo(
    () => players.filter((player) => selectedIds.includes(player.id)).slice(0, 3),
    [players, selectedIds]
  );

  const metricMaximums = useMemo(
    () =>
      METRICS.reduce((accumulator, metric) => {
        const values = players.map((player) => player.stats?.[metric.key] || 0);
        accumulator[metric.key] = Math.max(...values, 1);
        return accumulator;
      }, {}),
    [METRICS, players]
  );

  const radarData = useMemo(
    () =>
      METRICS.map((metric) => {
        const dataPoint = {
          metric: metric.label,
          rawValues: {},
        };

        selectedPlayers.forEach((player) => {
          const rawValue = player.stats?.[metric.key] || 0;
          dataPoint[player.name] = Math.round((rawValue / (metricMaximums[metric.key] || 1)) * 100);
          dataPoint.rawValues[player.name] = rawValue;
        });

        return dataPoint;
      }),
    [METRICS, metricMaximums, selectedPlayers]
  );

  return (
    <Card className="sc-chart-panel" variant="outlined">
      <CardHeader
        title={t('components.comparison.title')}
        subheader={t('components.comparison.subtitle')}
      />
      <CardContent>
        <Stack spacing={3}>
          <Autocomplete
            multiple
            disableCloseOnSelect
            limitTags={3}
            options={players}
            value={selectedPlayers}
            onChange={(_, value) => setSelectedIds(value.slice(0, 3).map((player) => player.id))}
            getOptionLabel={(option) => option?.name || "Jugador"}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderOption={(props, option) => {
              const { key, ...optionProps } = props;
              return (
                <Box component="li" key={key} {...optionProps} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Avatar sx={{ width: 28, height: 28, bgcolor: colors.primary[100], color: colors.primary[700] }}>
                    {option.name?.charAt(0)?.toUpperCase() || "?"}
                  </Avatar>
                  <Box>
                    <Typography variant="body2" fontWeight={600}>{option.name}</Typography>
                    <Typography variant="caption" color={'text.secondary'}>{option.team || t('components.comparison.noTeam')}</Typography>
                  </Box>
                </Box>
              );
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option.id}
                  label={option.name}
                  size="small"
                  sx={{ fontWeight: 600 }}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('components.comparison.selectPlayers')}
                placeholder={players.length ? t('components.comparison.placeholder') : t('components.comparison.noPlayers')}
              />
            )}
          />

          {selectedPlayers.length < 1 ? (
            <Alert severity="info" title={t('components.comparison.noData')}>
              {t('components.comparison.noData')}
            </Alert>
          ) : (
            <>
              <Box sx={{ height: { xs: 280, md: 300 } }} role="img" aria-label={t('components.comparison.title')}>
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={radarData} outerRadius="60%">
                    <PolarGrid stroke="var(--sc-line)" />
                    <PolarAngleAxis dataKey="metric" tick={{ fill: 'var(--sc-muted)', fontSize: 12 }} />
                    <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fill: 'var(--sc-muted)', fontSize: 11 }} />
                    <Tooltip content={<CustomTooltip />} />
                    {selectedPlayers.map((player, index) => (
                      <Radar
                        key={player.id}
                        name={player.name}
                        dataKey={player.name}
                        stroke={CHART_COLORS[index % CHART_COLORS.length]}
                        fill={CHART_COLORS[index % CHART_COLORS.length]}
                        fillOpacity={0.06}
                        strokeWidth={2}
                        strokeDasharray={index === 0 ? undefined : index === 1 ? "6 4" : "2 4"}
                        isAnimationActive={false}
                      />
                    ))}
                  </RadarChart>
                </ResponsiveContainer>
              </Box>

              <ul className="sc-radar-legend" aria-label={t('components.comparison.title')}>
                {selectedPlayers.map((player, index) => (
                  <li key={player.id}>
                    <span
                      aria-hidden="true"
                      style={{
                        borderTopColor: CHART_COLORS[index % CHART_COLORS.length],
                        borderTopStyle: index === 0 ? "solid" : index === 1 ? "dashed" : "dotted",
                      }}
                    />
                    {player.name}
                  </li>
                ))}
              </ul>

              <Typography variant="caption" color={'text.secondary'}>
                {t('components.comparison.radarCaption')}
              </Typography>

              <table className="sc-compare-table" aria-label={t('components.comparison.title')}>
                <thead>
                  <tr>
                    <th scope="col">{t('workspace.metric')}</th>
                    {selectedPlayers.map((player) => <th scope="col" key={player.id}>{player.name}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {METRICS.map((metric) => (
                    <tr key={metric.key}>
                      <th scope="row">{metric.label}</th>
                      {selectedPlayers.map((player) => <td key={player.id}>{player.stats?.[metric.key] || 0}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default PlayerComparisonChart;
